import gsap from 'gsap';
import {
  ANIMATIONS, BREAKPOINTS, CONFIG, KEYCODES,
} from '../config';
import getEventHandlerResize from '../ResizeHandler';

/**
 * used in Navigation and Header
 * if used in Header, this.$searchInput contains an actual input field, so we distinguish that at several points
 */
class BfvNavigationsSuche {
  constructor(options = {}) {
    this.$root = options.element;
    this.$searchInput = this.$root.querySelector('.js-navigation-search-input input');
    this.$clearButton = this.$root.querySelector('.js-navigation-search-clear');
    this.$searchButtons = this.$root.querySelectorAll('.js-navigation-search-button'); // might be more than one
    this.currentBreakpointKey = getEventHandlerResize().getCurrentViewport();
  }

  init() {
    this.bindEvents();

    // if site is loaded via browser back, show search input field with last content
    if (this.$searchInput.value && !this.$searchInput.classList.contains('js-show-input')) {
      this.openSearchWithoutAnimation();
    }
  }

  bindEvents() {
    window.addEventListener(CONFIG.events.VIEWPORT_CHANGED, (event) => {
      this.currentBreakpointKey = event.detail.viewport;
      if (this.$searchInput.value) { // redraw
        this.openSearchWithoutAnimation();
      }
    });

    if (this.$clearButton) {
      this.$clearButton.addEventListener('click', () => {
        this.toggleSearch();
      });
    }

    Array.prototype.slice.call(this.$searchButtons).forEach(($searchButton) => {
      $searchButton.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.$searchInput.value) {
          this.search();
        } else {
          this.toggleSearch();
        }
      });
    });

    // search on enter, close on escape
    this.$searchInput.addEventListener('keydown', (event) => {
      const key = event.keyCode || event.which;
      if (key === KEYCODES.ENTER) {
        event.preventDefault();
        this.search();
      }

      if (key === KEYCODES.ESCAPE) {
        if (this.$searchInput.classList.contains('js-show-input')) {
          this.toggleSearch();
        }
      }
    });
  }

  /**
   * show / hide search input field with animation
   * if gets hidden, also clear input
   */
  /* eslint-disable spaced-comment */
  toggleSearch() {
    if (!this.$searchInput.classList.contains('js-show-input')) {

      /** show input field **/

      const padding = this.getInputPadding();
      const height = this.getInputHeight();

      this.$searchInput.classList.add('js-show-input');
      this.$searchInput.focus();

      // initially, search field has now dimension
      gsap.set(this.$searchInput, {
        ease: ANIMATIONS.default.CURVE,
        height: '0px',
        padding: '0',
      });

      // first extend height
      gsap.to(this.$searchInput, ANIMATIONS.default.DURATION, {
        ease: ANIMATIONS.default.CURVE,
        height,
      });

      // then extend width
      // together with padding, so that the text does not slide too far to the left border
      gsap.to(this.$searchInput, ANIMATIONS.default.DURATION, {
        delay: ANIMATIONS.default.DURATION,
        ease: ANIMATIONS.default.CURVE,
        width: '100%',
        padding,
      });

      // finally show clear button
      gsap.delayedCall(2 * ANIMATIONS.default.DURATION, () => {
        this.$clearButton.classList.add('js-show-clear');
      });

    } else {

      /** clear and hide searchfield **/

      this.clearSearch();

      gsap.to(this.$searchInput, ANIMATIONS.default.DURATION, {
        ease: ANIMATIONS.default.CURVE,
        padding: 0,
        width: '0%',
      });

      gsap.delayedCall(ANIMATIONS.default.DURATION, () => this.$searchInput.classList.remove('js-show-input'));
    }

    this.$searchInput.setAttribute('display', 'block');
  }
  /* eslint-enable spaced-comment */

  /**
   * show search input field without animation
   */
  openSearchWithoutAnimation() {
    const padding = this.getInputPadding();
    this.$searchInput.classList.add('js-show-input');

    gsap.set(this.$searchInput, {
      ease: ANIMATIONS.default.CURVE,
      width: '100%',
      padding,
    });

    this.$clearButton.classList.add('js-show-clear');
    this.$searchInput.focus();
  }

  /**
   * @returns {string} the input padding depending on the viewport
   */
  getInputPadding() {
    switch (this.currentBreakpointKey) {
      case BREAKPOINTS.large:
        return '12px 34px 10px 16px';
      case BREAKPOINTS.medium:
        return '12px 44px 10px 16px';
      default:
        return '12px 32px 10px 10px';
    }
  }

  /**
   * @returns {string} the input height depending on the viewport
   */
  getInputHeight() {
    return this.currentBreakpointKey === BREAKPOINTS.large ? '36px' : '40px';
  }

  /**
   * deletes user input and hides clear button
   */
  clearSearch() {
    this.$clearButton.classList.remove('js-show-clear');
    this.$searchInput.value = '';
  }

  /**
   * redirects user to search results page with search term as query parameter
   */
  search() {
    const query = this.$searchInput.value;
    if (query) {
      window.location = `${this.$searchButtons.item(0).href}?query=${encodeURIComponent(query)}`;
    }
  }
}

export default BfvNavigationsSuche;
